import React, { FC, Suspense } from "react";

import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { Hidden } from "@mui/material";

import { AccessChatButton, HiddenOnScope, SectionTitle, SpinLoader } from "@/components/atoms";
import { InvoicePanel, ReportDamagePanel, TodoPanel, WaitingForBroker } from "@/components/features";
import { ClaimPanel } from "@/components/features/Claims";
import { shouldAllowConversations } from "@/components/features/Conversations/utils/shouldAllowConversations";
import { ErrorCard } from "@/components/organisms";
import { conversationApi } from "@/shared/apis";
import { Permissions, UserState } from "@/shared/domain";
import { isABroker, useAuth, useGlobalFilter, useNavigation } from "@/shared/hooks";

import QuickAccess from "./components/QuickAccess/QuickAccess";
import {
    StyledDashboardContainer,
    StyledHome,
    StyledHomeContainerCol,
    StyledHomeContainerRow,
    StyledHomesection,
} from "./styled";

const Dashboard: FC = () => {
    const { t } = useTranslation();
    const { user, userState } = useAuth();
    const { navToConversations } = useNavigation();
    const { isProEnvironment, policyholdersGF } = useGlobalFilter();

    const { data: numberOfUnreadMessages = 0 } = useQuery(
        "unread-conversations-" + user.currentBrokerId,
        () => conversationApi.getConversationsCount("unread"),
        {
            enabled: user.isAuthenticated && shouldAllowConversations(user, userState) && !isABroker(),
            useErrorBoundary: false,
            cacheTime: 0,
        },
    );

    if (!user.isAuthenticated) {
        return null;
    }
    if (userState === UserState.PENDING) {
        return <WaitingForBroker />;
    }

    const claimSection = (
        <>
            {user.someHasPermission(Permissions.ViewClaims, policyholdersGF) && (
                <StyledHomesection data-testid="home_section_claims">
                    <SectionTitle title={t("Common.Claims")} />
                    <ErrorBoundary resetKeys={[`claims-${user.currentBrokerId}`]} FallbackComponent={ErrorCard}>
                        <ClaimPanel />
                    </ErrorBoundary>
                </StyledHomesection>
            )}
        </>
    );
    const invoiceSection = (
        <>
            {user.someHasPermission(Permissions.ViewInvoices, policyholdersGF) && (
                <StyledHomesection data-testid="home_section_invoices">
                    <SectionTitle title={t("Invoices.Title")} />
                    <ErrorBoundary resetKeys={[`invoices-${user.currentBrokerId}`]} FallbackComponent={ErrorCard}>
                        <InvoicePanel />
                    </ErrorBoundary>
                </StyledHomesection>
            )}
        </>
    );

    const displayReportDamageSection =
        user.someHasPermission(Permissions.AccessDeclareClaim, policyholdersGF) ||
        user.someHasPermission(Permissions.CanDeclareSchoolClaim, policyholdersGF);
    return (
        <StyledDashboardContainer title="MyBroker" showGlobalFilter showBrokerInfos showEnvironmentSwitch>
            <StyledHome>
                <StyledHomeContainerRow>
                    <StyledHomeContainerCol>
                        {user.someHasPermission(Permissions.ViewLinkedDocuments, policyholdersGF) && (
                            <Hidden smUp>
                                <StyledHomesection data-testid="home_section_todo">
                                    <SectionTitle title={t("ToDos.Pending")} />
                                    <ErrorBoundary
                                        resetKeys={[`claims-${user.currentBrokerId}`]}
                                        FallbackComponent={ErrorCard}
                                    >
                                        <Suspense fallback={<SpinLoader />}>
                                            <TodoPanel />
                                        </Suspense>
                                    </ErrorBoundary>
                                </StyledHomesection>
                            </Hidden>
                        )}
                        <QuickAccess />
                        {!isProEnvironment && claimSection}
                    </StyledHomeContainerCol>
                    <StyledHomeContainerCol>
                        <Hidden smDown>
                            <StyledHomesection data-testid="home_section_todo">
                                <SectionTitle title={t("ToDos.Pending")} />
                                <ErrorBoundary
                                    resetKeys={[`claims-${user.currentBrokerId}`]}
                                    FallbackComponent={ErrorCard}
                                >
                                    <Suspense fallback={<SpinLoader />}>
                                        <TodoPanel />
                                    </Suspense>
                                </ErrorBoundary>
                            </StyledHomesection>
                        </Hidden>
                        {displayReportDamageSection && (
                            <StyledHomesection data-testid="home_section_report_damage">
                                <ReportDamagePanel />
                            </StyledHomesection>
                        )}

                        {!isProEnvironment && invoiceSection}
                    </StyledHomeContainerCol>
                </StyledHomeContainerRow>

                {isProEnvironment && claimSection}
                {isProEnvironment && invoiceSection}
            </StyledHome>
            {shouldAllowConversations(user, userState) && (
                <HiddenOnScope>
                    <AccessChatButton
                        onClick={navToConversations}
                        disabled={isABroker()}
                        numberOfUnreadMessages={numberOfUnreadMessages}
                    />
                </HiddenOnScope>
            )}
        </StyledDashboardContainer>
    );
};

export default Dashboard;
